import { useEffect, useState, useCallback, useRef } from "react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Field, Btn, Badge, Modal, inputCls } from "../components/common";
import { fmtIDR, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Plus, Upload, FileText } from "lucide-react";

const STATUS_LABEL = { menunggu_global_admin: "Menunggu Global Admin", menunggu_direktur: "Menunggu Direktur", menunggu_bukti_transfer: "Menunggu Bukti Transfer", dibayar: "Dibayar", ditolak: "Ditolak" };
const STATUS_TONE = { menunggu_global_admin: "amber", menunggu_direktur: "amber", menunggu_bukti_transfer: "cyan", dibayar: "emerald", ditolak: "rose" };
const empty = { judul: "", jumlah: "", penerima: "", bank: "", no_rekening: "", keterangan: "", akun_biaya: "", invoice_id: "" };

export default function Pengajuan() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [coa, setCoa] = useState([]);
  const [faktur, setFaktur] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState(empty);
  const [buktiFor, setBuktiFor] = useState(null);
  const [bukti, setBukti] = useState("");
  const buktiRef = useRef(null);
  const canUpload = ["Global Admin", "Super Admin"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/pengajuan").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/accounting/coa").then((r) => setCoa(r.data.filter((a) => a.category === "expense"))).catch(() => {});
    api.get("/pengajuan/faktur-belum-lunas").then((r) => setFaktur(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const pickFaktur = (id) => {
    const inv = faktur.find((x) => x.id === id);
    if (!inv) { setF({ ...f, invoice_id: "" }); return; }
    setF({ ...f, invoice_id: id, jumlah: inv.sisa, penerima: inv.vendor || "", judul: `Pembayaran faktur ${inv.number} — ${inv.vendor}` });
  };

  const submit = async () => {
    if (!f.judul.trim() || !f.penerima.trim() || !+f.jumlah || !f.akun_biaya) return toast.error("Judul, penerima, jumlah & akun biaya wajib diisi");
    try {
      await api.post("/pengajuan", { ...f, jumlah: +f.jumlah, invoice_id: f.invoice_id || null });
      toast.success("Pengajuan terkirim — menunggu approval Global Admin lalu Direktur");
      setShowForm(false); setF(empty); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const onBukti = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, 900 / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        setBukti(canvas.toDataURL("image/jpeg", 0.75));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const submitBukti = async () => {
    if (!bukti) return toast.error("Foto bukti transfer wajib diunggah");
    try {
      await api.put(`/pengajuan/${buktiFor.id}/bukti-transfer`, { bukti });
      toast.success("Bukti transfer tersimpan — faktur terkait otomatis terbayar & jurnal terbentuk");
      setBuktiFor(null); setBukti(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="pengajuan-page">
      <PageHeader title="Pengajuan Pembayaran" subtitle="Admin Lokasi input → Global Admin cek → Direktur setuju → Global Admin lampirkan bukti transfer → jurnal & faktur otomatis terbayar" testid="pengajuan-header">
        <Btn onClick={() => setShowForm(true)} data-testid="pengajuan-add"><Plus className="w-4 h-4" /> Pengajuan Baru</Btn>
      </PageHeader>
      <div className="space-y-2">
        {rows.map((r) => (
          <Card key={r.id} className="!p-4" testid={`pengajuan-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-slate-500">{r.number}</span>
              <span className="font-medium text-slate-200 flex-1 min-w-[160px]">{r.judul}</span>
              <Badge tone={STATUS_TONE[r.status] || "slate"} data-testid={`pengajuan-status-${r.id}`}>{STATUS_LABEL[r.status] || r.status}</Badge>
              <span className="font-mono text-sm text-slate-200">{fmtIDR(r.jumlah)}</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5">
              {r.penerima} {r.bank ? `· ${r.bank} ${r.no_rekening}` : ""} · oleh {r.created_by_name} · {fmtDate((r.created_at || "").slice(0, 10))}
              {r.keterangan ? ` — ${r.keterangan}` : ""}
              {r.invoice_id && <span className="text-cyan-400"> · terhubung faktur</span>}
              {r.paid_by ? ` · bukti transfer oleh ${r.paid_by}` : ""}
            </div>
            {r.status === "menunggu_bukti_transfer" && canUpload && (
              <div className="mt-3">
                <Btn className="!text-xs !py-1.5" onClick={() => { setBuktiFor(r); setBukti(""); }} data-testid={`pengajuan-bukti-${r.id}`}><Upload className="w-3.5 h-3.5" /> Lampirkan Bukti Transfer</Btn>
              </div>
            )}
          </Card>
        ))}
        {rows.length === 0 && <p className="text-slate-500 text-sm">Belum ada pengajuan.</p>}
      </div>

      <Modal open={showForm} onClose={() => setShowForm(false)} title="Pengajuan Pembayaran Baru" testid="pengajuan-modal">
        <div className="space-y-3">
          <Field label="Ambil dari Faktur Pembelian (opsional)">
            <select className={inputCls} value={f.invoice_id} onChange={(e) => pickFaktur(e.target.value)} data-testid="pj-faktur">
              <option value="">— Tanpa faktur (pengeluaran umum) —</option>
              {faktur.map((x) => <option key={x.id} value={x.id}>{x.number} — {x.vendor} (sisa {fmtIDR(x.sisa)})</option>)}
            </select>
          </Field>
          <Field label="Judul Pengajuan *"><input className={inputCls} value={f.judul} onChange={(e) => setF({ ...f, judul: e.target.value })} data-testid="pj-judul" placeholder="Contoh: Pembayaran material batch 3" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Jumlah (Rp) *"><input type="number" className={inputCls} value={f.jumlah} onChange={(e) => setF({ ...f, jumlah: e.target.value })} data-testid="pj-jumlah" /></Field>
            <Field label="Akun Biaya *">
              <select className={inputCls} value={f.akun_biaya} onChange={(e) => setF({ ...f, akun_biaya: e.target.value })} data-testid="pj-akun">
                <option value="">Pilih akun biaya...</option>
                {coa.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
              </select>
            </Field>
            <Field label="Penerima *"><input className={inputCls} value={f.penerima} onChange={(e) => setF({ ...f, penerima: e.target.value })} data-testid="pj-penerima" /></Field>
            <Field label="Bank"><input className={inputCls} value={f.bank} onChange={(e) => setF({ ...f, bank: e.target.value })} /></Field>
            <Field label="No. Rekening"><input className={inputCls} value={f.no_rekening} onChange={(e) => setF({ ...f, no_rekening: e.target.value })} /></Field>
          </div>
          <Field label="Keterangan"><textarea className={inputCls} rows={2} value={f.keterangan} onChange={(e) => setF({ ...f, keterangan: e.target.value })} /></Field>
          <div className="flex justify-end gap-2"><Btn tone="ghost" onClick={() => setShowForm(false)}>Batal</Btn><Btn onClick={submit} data-testid="pj-submit">Kirim Pengajuan</Btn></div>
        </div>
      </Modal>

      <Modal open={!!buktiFor} onClose={() => setBuktiFor(null)} title={`Bukti Transfer — ${buktiFor?.number || ""}`} testid="bukti-transfer-modal">
        <div className="space-y-3">
          <p className="text-xs text-slate-400">Unggah foto/scan bukti transfer untuk <span className="text-slate-200 font-medium">"{buktiFor?.judul}"</span> ({fmtIDR(buktiFor?.jumlah || 0)}). Setelah tersimpan: jurnal akuntansi terbentuk & faktur terkait otomatis berstatus terbayar.</p>
          <input ref={buktiRef} type="file" accept="image/*" onChange={onBukti} className="hidden" data-testid="bukti-file-input" />
          {bukti ? (
            <div>
              <img src={bukti} alt="bukti transfer" className="w-full rounded-lg border border-slate-700" data-testid="bukti-preview" />
              <Btn tone="ghost" className="mt-2 !text-xs" onClick={() => { setBukti(""); buktiRef.current.value = ""; }}>Ganti File</Btn>
            </div>
          ) : (
            <button onClick={() => buktiRef.current.click()} data-testid="bukti-upload-button"
              className="w-full border-2 border-dashed border-slate-700 hover:border-emerald-500/50 rounded-lg py-8 text-slate-500 hover:text-emerald-300 transition-colors flex flex-col items-center gap-2">
              <FileText className="w-6 h-6" />
              <span className="text-xs">Unggah foto bukti transfer</span>
            </button>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Btn tone="ghost" onClick={() => setBuktiFor(null)}>Batal</Btn>
            <Btn onClick={submitBukti} disabled={!bukti} data-testid="bukti-submit-button">Simpan & Proses Pembayaran</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
